import React, { useState, useContext } from "react";
import Header from "../components/Header";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import ScrollToTopButton from "../components/ScrollTop";
import WhatsAppMsg from "../components/WhatsApp";
import { downloadsData } from "../utils/downloadsData";
import { IoSearchOutline } from "react-icons/io5";
import { FaArrowRightLong } from "react-icons/fa6";
import { UserContext } from "../utils/UserContext"; // Adjust the import path as needed

const Downloads = () => {
  // Access user and modal control from context
  const { user, openSignInModal } = useContext(UserContext);

  // State for search text, selected category, and filtered results
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredData, setFilteredData] = useState(downloadsData);

  // Get unique categories for the dropdown
  const categories = [...new Set(downloadsData.map((item) => item.category))];

  // Handle the search button click
  const handleSearch = () => {
    const lowerSearch = searchTerm.toLowerCase();
    const newData = downloadsData.filter((item) => {
      const nameMatch = item.productName.toLowerCase().includes(lowerSearch);
      const categoryMatch = item.category.toLowerCase().includes(lowerSearch);
      return nameMatch || categoryMatch;
    });
    setFilteredData(newData);
    setSelectedCategory("");
  };

  // Handle changes in the dropdown (filter by category)
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (!category) {
      setFilteredData(downloadsData);
      return;
    }
    const newData = downloadsData.filter((item) => item.category === category);
    setFilteredData(newData);
    setSearchTerm("");
  };

  // Handler for download link clicks
  const handleDownloadClick = (e) => {
    if (!user) {
      e.preventDefault();
      openSignInModal();
    }
  };

  return (
    <>
      <div className="about-us-page">
        <Header />
      </div>
      <Banner
        imgSrc="https://content.qeeb.in/coltec/download/download-center-banner.webp"
        altText="Download Center | COLTEC-GLOBAL"
        bannerHeight="md:h-[470px] h-[300px]"
        imgHeight="md:inset-auto pt-8 md:h-[470px] md:pt-0 h-[300px]"
        heading="Download Center | COLTEC-GLOBAL"
        headClasses="md:block text-center hidden max-w-2xl text-[21px] font-semibold tracking-tight leading-[1.7] text-white sm:text-4xl drop-shadow-lg mt-3"
        opacityClasses="opacity-layer absolute inset-0 w-full md:h-full h-[300px] bg-black/60 z-0"
        paragraphText="Get product brochures, technical data sheets, user manuals, and specifications for COLTEC-GLOBAL’s display technology solutions. Access all the resources you need in one place for seamless integration and installation."
        positionClasses="absolute md:bottom-32 bottom-12 left-0 lg:inset-x-0 flex"
        headerTextClasses="text-[29px] md:text-4xl max-w-2xl text-white font-medium leading-10"
        paragraphTextClasses="md:text-base text-center md:block hidden text-sm text-white max-w-2xl mt-4"
      />
      {/* Mobile Banner Text */}
      <div className="md:hidden block px-6 py-6 bg-gray-50">
        <h2 className="md:w-[362px] relative justify-start text-black mb-4 text-base font-medium font-['Poppins'] leading-tight">
          Download Center | COLTEC-GLOBAL
        </h2>
        <p className="max-w-[373px] relative justify-start text-black text-xs md:text-sm font-normal font-['Poppins'] leading-[19px]">
          Get product brochures, technical data sheets, user manuals, and
          specifications for COLTEC-GLOBAL’s display technology solutions.
          Access all the resources you need in one place for seamless
          integration and installation.
        </p>
      </div>

      <div className="px-6 lg:px-8 md:py-14 pt-8 pb-10 max-w-7xl m-auto book-demo-page">
        {/* SEARCH & FILTER SECTION */}
        <div className="flex flex-col md:flex-row justify-center items-start md:items-center space-y-4 md:space-y-0 md:space-x-8 mb-16">
          {/* Search Box */}
          <div className="flex">
            <div className="relative">
              <IoSearchOutline className="absolute left-3 text-[#737373] top-3.5 md:top-2.5" />
              <input
                type="text"
                placeholder="Search by product or category"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border md:w-[300px] w-full border-gray-300 rounded-l-md pl-8 placeholder:text-[11px] md:placeholder:text-sm md:text-sm pr-3 py-2"
              />
            </div>
            <button
              onClick={handleSearch}
              className="flex items-center bg-[#3b82f6] hover:bg-[#3b83f6f5] text-sm font-normal leading-normal text-white px-6 md:px-8 py-2 rounded-r"
            >
              Search <FaArrowRightLong className="ml-3" />
            </button>
          </div>

          {/* Filter by Category */}
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="border border-gray-300 px-3 py-2 text-[#737373] text-sm rounded-md"
            >
              <option value="">Filter by Product</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* PRODUCT LISTING */}
        <div className="grid md:grid-cols-2 md:gap-16 grid-cols-1 gap-6">
          {filteredData.map((product, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-[0px_4px_15px_0px_rgba(10,10,10,0.05)] border border-neutral-200 p-6"
            >
              <div className="flex flex-col md:flex-row md:items-center md:space-x-5">
                {/* Product Image */}
                <div>
                  <img
                    src={product.imageUrl}
                    alt={product.productName}
                    className="w-64 h-[155px] object-fill mb-4 md:mb-0"
                  />
                </div>
                {/* Product Info */}
                <div>
                  <h3 className="justify-center text-black text-2xl font-medium font-['Poppins'] leading-7 mb-4">
                    {product.productName}
                  </h3>
                  <p className="justify-center text-gray-700 text-sm font-medium font-['Poppins'] leading-none mb-3">
                    Product:{" "}
                    <span className="text-black">{product.category}</span>
                  </p>
                  <p className="text-xs font-medium mb-1 mt-10">Downloads</p>
                  <div className="flex flex-wrap font-medium text-xs items-center">
                    <a
                      href={product.catalogueUrl}
                      onClick={handleDownloadClick}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600"
                    >
                      {product.catalogue}
                    </a>
                    <span className="mx-1">|</span>
                    <a
                      href={product.datasheetUrl}
                      onClick={handleDownloadClick}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600"
                    >
                      {product.datasheet}
                    </a>
                    <span className="mx-1">|</span>
                    <a
                      href={product.firmwareUrl}
                      onClick={handleDownloadClick}
                      download
                      className="text-blue-600"
                    >
                      {product.firmware}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
      <ScrollToTopButton />
      <WhatsAppMsg />
    </>
  );
};

export default Downloads;
